/**
 * circuitService — resolve and fetch a circuit outline for the live map.
 * -------------------------------------------------------------------------
 * The circuit SHAPE (a lon/lat ring) comes from bacinger/f1-circuits, mirrored
 * on the jsDelivr CDN (CORS-open, edge-cached, MIT-licensed). We keep a small
 * vendored index (circuitLocations.js) so resolving a session → file id costs
 * no network round-trip; only the chosen circuit's GeoJSON is fetched.
 *
 * This is the KEY-FREE backdrop layer: it always works, live session or not,
 * and needs no OpenF1 access. The live car dots are a separate concern
 * (openF1LiveClient.js) drawn in their own telemetry frame.
 */
import { CIRCUIT_LOCATIONS } from '../data/circuitLocations.js';
import { matchCircuit } from './circuitGeometry.js';
import { fetchSeasonRaces } from './jolpicaClient.js';

const CDN_BASE = 'https://cdn.jsdelivr.net/gh/bacinger/f1-circuits@master/circuits';
const CACHE = new Map();
const TTL_MS = 24 * 60 * 60 * 1000; // circuit shapes never change intra-day

/**
 * fetchCircuitOutline — GeoJSON ring for one circuit id (e.g. "hu-1986").
 * @returns {Promise<{id:string,name:string,location:string,coordinates:Array<[number,number]>}|null>}
 */
export async function fetchCircuitOutline(id, { signal } = {}) {
  if (!id) return null;
  const cached = CACHE.get(id);
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  let res;
  try {
    res = await fetch(`${CDN_BASE}/${id}.geojson`, { signal, headers: { Accept: 'application/json' } });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return null;
  }
  if (!res.ok) return null;

  let geo;
  try {
    geo = await res.json();
  } catch {
    return null;
  }
  const feature = geo?.features?.[0];
  const coords = feature?.geometry?.coordinates;
  if (!Array.isArray(coords) || coords.length < 3) return null;

  const value = {
    id,
    name: feature.properties?.Name ?? feature.properties?.name ?? id,
    location: feature.properties?.Location ?? '',
    coordinates: coords,
  };
  CACHE.set(id, { value, expiresAt: Date.now() + TTL_MS });
  return value;
}

/**
 * resolveCircuitForSession — match an OpenF1 session to a circuit and fetch it.
 * Returns null when the venue isn't in the vendored index (rare — the index
 * covers the current calendar) or the fetch fails.
 */
export async function resolveCircuitForSession(session, { signal } = {}) {
  const match = matchCircuit(session, CIRCUIT_LOCATIONS);
  if (!match) return null;
  const outline = await fetchCircuitOutline(match.id, { signal });
  if (!outline) return { ...match, coordinates: null };
  return outline;
}

/**
 * fetchCurrentGpCircuit — best-effort circuit for the "locked" live state,
 * where OpenF1 won't name the session. Finds the race whose weekend spans now
 * from the Jolpica schedule (key-free) and resolves its circuit outline.
 *
 * @returns {Promise<({id,name,location,coordinates}&{raceName?:string})|null>}
 */
export async function fetchCurrentGpCircuit({ signal, now = new Date() } = {}) {
  const season = now.getUTCFullYear();
  let races;
  try {
    races = await fetchSeasonRaces(season, { signal });
  } catch {
    return null;
  }
  const t = now.getTime();
  const DAY = 86_400_000;
  let current = null;
  for (const race of races) {
    const raceMs = Date.parse(`${race.date}T${race.time || '13:00:00Z'}`);
    if (!Number.isFinite(raceMs)) continue;
    // Weekend window: ~Thursday (track activity begins) to a few hours post-race.
    if (t >= raceMs - 3.5 * DAY && t <= raceMs + 0.25 * DAY) {
      current = race;
      break;
    }
  }
  if (!current) return null;

  const circuit = await resolveCircuitForSession(
    {
      location: current.Circuit?.Location?.locality,
      circuit_short_name: current.Circuit?.circuitName,
      country_name: current.Circuit?.Location?.country,
    },
    { signal }
  );
  return circuit ? { ...circuit, raceName: current.raceName } : null;
}
